import Head from 'next/head';

import type { GuidePageContent } from '_features/GuidePage/types';

interface GuideJsonLdProps {
  content: GuidePageContent;
  canonical: string;
  origin: string;
}

export const GuideJsonLd = (props: GuideJsonLdProps) => {
  const { content, canonical, origin } = props;
  const authorUrl = content.author.href.startsWith('http') ? content.author.href : `${origin}${content.author.href}`;
  const graph = [
    {
      '@type': 'Organization',
      '@id': `${origin}/#organization`,
      name: 'Sortlist',
      url: `${origin}/`,
    },
    {
      '@type': 'Person',
      '@id': `${authorUrl}#person`,
      name: content.author.name,
      jobTitle: content.author.role,
      url: authorUrl,
      image: content.author.avatarUrl,
      sameAs: content.author.links.map((link) => link.href),
      worksFor: { '@id': `${origin}/#organization` },
    },
    {
      '@type': 'Article',
      '@id': `${canonical}#article`,
      mainEntityOfPage: { '@id': `${canonical}#webpage` },
      headline: content.header.title,
      description: content.seo.description,
      inLanguage: content.locale,
      isAccessibleForFree: true,
      datePublished: content.publishedAt,
      dateModified: content.updatedAt,
      author: { '@id': `${authorUrl}#person` },
      publisher: { '@id': `${origin}/#organization` },
      ...(content.about != null ? { about: { '@type': 'Thing', name: content.about } } : {}),
      ...(content.citations.length > 0 ? { citation: content.citations } : {}),
    },
    {
      '@type': 'WebPage',
      '@id': `${canonical}#webpage`,
      url: canonical,
      name: content.seo.title,
      inLanguage: content.locale,
      isPartOf: { '@id': `${origin}/#website` },
      breadcrumb: { '@id': `${canonical}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: content.breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.label,
        item: crumb.href.startsWith('http') ? crumb.href : `${origin}${crumb.href}`,
      })),
    },
    {
      '@type': 'FAQPage',
      '@id': `${canonical}#faq`,
      mainEntity: content.faq.items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ];

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }) }}
      />
    </Head>
  );
};
